import React from 'react';
import Icon from '../../../components/AppIcon';

const UserStats = ({ users }) => {
  const totalUsers = users?.length;
  const activeUsers = users?.filter(user => user?.status === 'active')?.length;
  const pendingUsers = users?.filter(user => user?.status === 'pending')?.length;
  const suspendedUsers = users?.filter(user => user?.status === 'suspended')?.length;

  const roleDistribution = users?.reduce((acc, user) => {
    acc[user.role] = (acc?.[user?.role] || 0) + 1;
    return acc;
  }, {});

  const recentLogins = users?.filter(user => {
    const lastLogin = new Date(user.lastLoginDate);
    const dayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return lastLogin > dayAgo;
  })?.length;

  const stats = [
    {
      label: 'Total Users',
      value: totalUsers,
      icon: 'Users',
      color: 'bg-primary',
      textColor: 'text-primary'
    },
    {
      label: 'Active Users',
      value: activeUsers,
      icon: 'UserCheck',
      color: 'bg-success',
      textColor: 'text-success'
    },
    {
      label: 'Pending Approval',
      value: pendingUsers,
      icon: 'Clock',
      color: 'bg-warning',
      textColor: 'text-warning'
    },
    {
      label: 'Suspended',
      value: suspendedUsers,
      icon: 'UserX',
      color: 'bg-error',
      textColor: 'text-error'
    }
  ];

  const topRoles = Object.entries(roleDistribution)?.sort(([,a], [,b]) => b - a)?.slice(0, 4);

  const getRoleLabel = (role) => {
    const roleLabels = {
      owner: 'Owner/Executive',
      chartering: 'Chartering Desk',
      finance: 'Finance Team',
      compliance: 'Compliance Officer',
      operations: 'Operations Manager',
      technical: 'Technical Superintendent',
      admin: 'System Administrator'
    };
    return roleLabels?.[role] || role;
  };

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats?.map((stat) => (
          <div key={stat?.label} className="bg-card rounded-lg border border-border shadow-maritime-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{stat?.label}</p>
                <p className="text-2xl font-bold text-foreground mt-1">{stat?.value}</p>
              </div>
              <div className={`w-12 h-12 rounded-lg ${stat?.color}/10 flex items-center justify-center`}>
                <Icon name={stat?.icon} size={24} strokeWidth={2} className={stat?.textColor} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Additional Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Role Distribution */}
        <div className="bg-card rounded-lg border border-border shadow-maritime-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="PieChart" size={20} strokeWidth={2} className="text-accent" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Role Distribution</h3>
              <p className="text-sm text-muted-foreground">Users by role type</p>
            </div>
          </div>

          <div className="space-y-3">
            {topRoles?.map(([role, count]) => {
              const percentage = Math.round((count / totalUsers) * 100);
              return (
                <div key={role} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground">
                      {getRoleLabel(role)}
                    </span>
                    <span className="text-sm text-muted-foreground">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-accent h-2 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Activity Summary */}
        <div className="bg-card rounded-lg border border-border shadow-maritime-sm p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} strokeWidth={2} className="text-success" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Activity Summary</h3>
              <p className="text-sm text-muted-foreground">Recent user activity</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="LogIn" size={16} strokeWidth={2} className="text-success" />
                <span className="text-sm font-medium text-foreground">Recent Logins</span>
              </div>
              <span className="text-sm font-bold text-foreground">{recentLogins}</span>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="UserPlus" size={16} strokeWidth={2} className="text-accent" />
                <span className="text-sm font-medium text-foreground">New This Week</span>
              </div>
              <span className="text-sm font-bold text-foreground">
                {users?.filter(user => {
                  const created = new Date(user.createdAt);
                  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
                  return created > weekAgo;
                })?.length}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name="Shield" size={16} strokeWidth={2} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Security Events</span>
              </div>
              <span className="text-sm font-bold text-foreground">3</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStats;